import React from "react";
import { Chip, Group, Stack, Text, Tooltip } from "@mantine/core";
import type { MantineSize } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  DAY_ALLOWED_KEYS,
  DAY_ALLOWED_OPTIONS,
  DayAllowedFlags,
  DayAllowedKey,
} from "./dayAvailability";

interface WeekdayToggleProps {
  /** Current day-of-week flags (true = available that day). */
  value: DayAllowedFlags;
  /** Called with the full updated flag set whenever a day is toggled. */
  onChange: (flags: DayAllowedFlags) => void;
  label?: string;
  description?: string;
  tooltip?: string;
  size?: MantineSize;
  disabled?: boolean;
}

/**
 * Multi-select chip group for choosing which days of the week a service is
 * available. Selecting a chip marks that day available; clearing it makes the
 * service unbookable on that day.
 */
export default function WeekdayToggle({
  value,
  onChange,
  label = "Available days",
  description,
  tooltip,
  size = "sm",
  disabled = false,
}: WeekdayToggleProps) {
  const selected = DAY_ALLOWED_KEYS.filter((key) => value[key]);

  const handleChange = (nextSelected: string[]) => {
    const selectedSet = new Set(nextSelected);
    const flags = {} as DayAllowedFlags;
    for (const key of DAY_ALLOWED_KEYS) {
      flags[key] = selectedSet.has(key);
    }
    onChange(flags);
  };

  return (
    <Stack gap={4}>
      {label && (
        <Group gap={4} align="center">
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {tooltip && (
            <Tooltip label={tooltip} position="right" multiline w={260}>
              <IconInfoCircle
                size={16}
                style={{ display: "inline-block", cursor: "help" }}
              />
            </Tooltip>
          )}
        </Group>
      )}
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
      <Chip.Group multiple value={selected} onChange={handleChange}>
        <Group gap="xs">
          {DAY_ALLOWED_OPTIONS.map((option) => (
            <Chip
              key={option.key}
              value={option.key}
              size={size}
              color="var(--brand-teal)"
              variant="filled"
              disabled={disabled}
            >
              {option.short}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Stack>
  );
}
